import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { ScanLine, ReceiptText, ArrowUpCircle, CheckCircle, ChevronRight } from "lucide-react";

interface WorkflowStep {
  id: string;
  label: string;
  description: string;
  icon: any;
  status: 'pending' | 'active' | 'completed';
}

interface WorkflowStepsProps {
  activeView: string;
  onViewChange: (view: string) => void;
  completionPercentage?: number;
  className?: string;
}

export const WorkflowSteps = ({ 
  activeView, 
  onViewChange,
  completionPercentage = 0,
  className 
}: WorkflowStepsProps) => {
  const steps: WorkflowStep[] = [
    {
      id: 'scan',
      label: 'Scan & Review',
      description: 'Scan products and verify data quality',
      icon: ScanLine,
      status: activeView === 'scan' ? 'active' : 'completed'
    },
    {
      id: 'invoice',
      label: 'Invoice Match',
      description: 'Match products with invoice items',
      icon: ReceiptText,
      status: activeView === 'invoice' ? 'active' : 
              activeView === 'submit' ? 'completed' : 'pending'
    },
    {
      id: 'submit',
      label: 'Submit',
      description: 'Final review and submit to POS',
      icon: ArrowUpCircle,
      status: activeView === 'submit' ? 'active' : 'pending'
    }
  ];

  const getStepColor = (step: WorkflowStep) => {
    switch (step.id) {
      case 'scan':
        return 'text-info';
      case 'invoice':
        return 'text-warning';
      case 'submit':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStepClasses = (step: WorkflowStep, stepNumber: number) => {
    const baseClasses = "group relative flex-1 p-4 min-h-[80px] transition-all duration-300 hover:shadow-lg cursor-pointer rounded-xl border-2 transform hover:scale-[1.02]";
    
    switch (step.status) {
      case 'active':
        return cn(
          baseClasses, 
          "bg-gradient-to-br from-primary/15 to-primary/5 border-primary/40 shadow-lg ring-2 ring-primary/25",
          "before:absolute before:top-2 before:left-2 before:w-6 before:h-6 before:rounded-full before:bg-primary before:text-primary-foreground before:flex before:items-center before:justify-center before:text-xs before:font-bold before:shadow-md",
          `before:content-['${stepNumber}']`,
          "after:absolute after:top-2 after:right-2 after:w-2 after:h-2 after:rounded-full after:bg-primary/30 after:animate-pulse"
        );
      case 'completed':
        return cn(
          baseClasses, 
          "bg-gradient-to-br from-success/15 to-success/5 border-success/40 hover:from-success/20 hover:to-success/10",
          "before:absolute before:top-2 before:left-2 before:w-6 before:h-6 before:rounded-full before:bg-success before:text-success-foreground before:flex before:items-center before:justify-center before:text-xs before:font-bold before:shadow-md",
          "before:content-['✓']"
        );
      default:
        return cn(
          baseClasses, 
          "bg-gradient-to-br from-muted/60 to-muted/30 border-muted/60 hover:from-muted/70 hover:to-muted/40 opacity-75",
          "before:absolute before:top-2 before:left-2 before:w-6 before:h-6 before:rounded-full before:bg-muted-foreground/20 before:text-muted-foreground before:flex before:items-center before:justify-center before:text-xs before:font-semibold",
          `before:content-['${stepNumber}']`
        );
    }
  };

  const getIconClasses = (step: WorkflowStep) => {
    const stepColor = getStepColor(step);
    
    switch (step.status) {
      case 'active':
        return cn("w-5 h-5", stepColor);
      case 'completed':
        return "w-5 h-5 text-success";
      default:
        return "w-5 h-5 text-muted-foreground";
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Workflow Progress</span>
          <span className="font-medium">{completionPercentage}% Complete</span>
        </div>
        <Progress value={completionPercentage} className="h-2" />
      </div>

      {/* Step Navigation */}
      <Card className="shadow-sm border-border/50">
        <CardContent className="p-3">
          <div className="flex gap-2">
            {steps.map((step, index) => {
              const Icon = step.status === 'completed' ? CheckCircle : step.icon;
              const stepNumber = index + 1;
              
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <Button
                    variant="ghost"
                    onClick={() => onViewChange(step.id)}
                    className={getStepClasses(step, stepNumber)}
                  >
                    <div className="flex items-center gap-3 w-full pl-8">
                      <Icon className={cn(getIconClasses(step), "transition-transform group-hover:scale-110 flex-shrink-0")} />
                      <div className="text-left flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate leading-tight">{step.label}</div>
                        <div className="text-xs opacity-70 truncate leading-tight">{step.description}</div>
                      </div>
                      {step.status === 'active' && (
                        <div className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full font-medium flex-shrink-0">
                          Active
                        </div>
                      )}
                    </div>
                  </Button>
                  
                  {/* Enhanced Connector Arrow */}
                  {index < steps.length - 1 && (
                    <div className="w-10 flex justify-center items-center">
                      <ChevronRight className={cn(
                        "w-6 h-6 transition-all duration-300",
                        step.status === 'completed' ? "text-success animate-pulse" : 
                        step.status === 'active' ? "text-primary" : "text-muted-foreground/40"
                      )} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};